import React, { useState, useEffect } from 'react';
import { 
  Row, 
  Col, 
  Card, 
  Statistic, 
  Typography, 
  Space,
  Spin,
  message
} from 'antd';
import { 
  ShoppingOutlined, 
  InboxOutlined, 
  CheckCircleOutlined, 
  TruckOutlined, 
  CloseCircleOutlined, 
  FileTextOutlined, 
  UserOutlined,
  ClockCircleOutlined,
  CheckOutlined,
  PlusOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import fundoNovagGeracao from '../images/FUNDO_NOVAGERACAO_SEMPRE.png';
import { buildApiUrl } from '../utils/api';
import './AdminDashboard.css';

const { Title, Paragraph } = Typography;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProdutos: 0,
    totalPedidos: 0,
    pedidosPendentes: 0,
    pedidosAprovados: 0,
    pedidosEmTransporte: 0,
    pedidosEntregues: 0,
    pedidosCancelados: 0,
    totalUsuarios: 0
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [produtosResponse, pedidosResponse, usuariosResponse] = await Promise.all([
        fetch(buildApiUrl('/api/admin/produtos'), {
        headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(buildApiUrl('/api/admin/pedidos'), {
        headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(buildApiUrl('/api/users'), {
        headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (produtosResponse.ok && pedidosResponse.ok && usuariosResponse.ok) {
        const produtosData = await produtosResponse.json();
        const pedidosData = await pedidosResponse.json();
        const usuariosData = await usuariosResponse.json();
        
        const pedidos = pedidosData.pedidos || [];
        const usuarios = usuariosData.users || [];
        
        setStats({
          totalProdutos: produtosData.produtos?.length || 0,
          totalPedidos: pedidos.length,
          pedidosPendentes: pedidos.filter(p => p.status === 'Pendente de Aprovação').length,
          pedidosAprovados: pedidos.filter(p => p.status === 'Aprovado').length,
          pedidosEmTransporte: pedidos.filter(p => p.status === 'Em Transporte').length,
          pedidosEntregues: pedidos.filter(p => p.status === 'Entregue').length,
          pedidosCancelados: pedidos.filter(p => p.status === 'Cancelado').length,
          totalUsuarios: usuarios.length
        });
      }
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      message.error('Erro ao carregar estatísticas do sistema');
    } finally {
      setLoading(false);
    }
  };

  const statsCards = [
    {
      title: 'Total de Produtos',
      value: stats.totalProdutos,
      icon: <ShoppingOutlined />,
      color: '#3e79f7'
    },
    {
      title: 'Total de Pedidos',
      value: stats.totalPedidos,
      icon: <InboxOutlined />,
      color: '#52c41a'
    },
    {
      title: 'Pedidos Pendentes',
      value: stats.pedidosPendentes,
      icon: <ClockCircleOutlined />,
      color: '#faad14'
    },
    {
      title: 'Pedidos Aprovados',
      value: stats.pedidosAprovados,
      icon: <CheckCircleOutlined />,
      color: '#52c41a'
    },
    {
      title: 'Em Transporte',
      value: stats.pedidosEmTransporte,
      icon: <TruckOutlined />,
      color: '#1890ff'
    },
    {
      title: 'Pedidos Entregues',
      value: stats.pedidosEntregues,
      icon: <CheckOutlined />,
      color: '#52c41a'
    },
    {
      title: 'Pedidos Cancelados',
      value: stats.pedidosCancelados,
      icon: <CloseCircleOutlined />,
      color: '#ff4d4f'
    },
    {
      title: 'Total de Usuários',
      value: stats.totalUsuarios,
      icon: <UserOutlined />,
      color: '#722ed1'
    }
  ];

  const quickActions = [
    {
      title: 'Gerenciar Produtos',
      description: 'Adicionar, editar ou remover produtos da loja',
      icon: <PlusOutlined />,
      path: '/admin-produtos',
      color: '#3e79f7'
    },
    {
      title: 'Gerenciar Pedidos',
      description: 'Visualizar e atualizar status dos pedidos',
      icon: <FileTextOutlined />,
      path: '/admin-pedidos',
      color: '#52c41a'
    },
    {
      title: 'Gerenciar Usuários',
      description: 'Visualizar e gerenciar usuários do sistema',
      icon: <UserOutlined />,
      path: '/users',
      color: '#722ed1'
    },
    {
      title: 'Logs do Sistema',
      description: 'Visualizar logs de atividades do sistema',
      icon: <BarChartOutlined />,
      path: '/logs',
      color: '#faad14'
    }
  ];

  if (loading) {
    return (
      <Row justify="center" align="middle" style={{ minHeight: '100vh' }}>
        <Spin size="large" />
      </Row>
    );
  }

  return (
    <Space direction="vertical" size={24} style={{ width: '100%', padding: '24px', background: '#fff', minHeight: '100vh' }}>
      {/* Header com Background */}
      <Card
        bordered={false}
        bodyStyle={{
          padding: 0,
          background: `linear-gradient(135deg, rgba(62, 121, 247, 0.95) 0%, rgba(30, 90, 220, 0.95) 100%)`,
          backgroundImage: `url(${fundoNovagGeracao})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundBlendMode: 'overlay',
          borderRadius: '8px',
          minHeight: '140px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Row justify="center" align="middle" style={{ width: '100%', padding: '40px 40px' }}>
          <Col style={{ textAlign: 'center' }}>
            <Space direction="vertical" size={12}>
              <Title level={1} style={{ color: '#fff', margin: 0, fontWeight: 700, fontSize: '42px' }}>
                Painel de Administração
              </Title>
              <Paragraph style={{ color: 'rgba(255, 255, 255, 0.95)', margin: 0, fontSize: '20px', fontWeight: 400 }}>
                Gerencie todos os aspectos do sistema
              </Paragraph>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Estatísticas */}
      <Row gutter={[16, 16]}>
        {statsCards.map((stat, index) => (
          <Col xs={24} sm={12} md={8} lg={6} key={index}>
            <Card hoverable style={{ borderRadius: 8 }}>
              <Statistic
                title={stat.title}
                value={stat.value}
                prefix={React.cloneElement(stat.icon, { 
                  style: { color: stat.color, fontSize: 24 } 
                })}
                valueStyle={{ color: stat.color, fontWeight: 600 }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Ações Rápidas */}
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Title level={3}>Ações Rápidas</Title>
        <Row gutter={[16, 16]}>
          {quickActions.map((action, index) => (
            <Col xs={24} sm={12} md={8} lg={6} key={index}>
              <Card
                hoverable
                style={{ 
                  borderRadius: 8,
                  textAlign: 'center',
                  height: '100%'
                }}
                onClick={() => navigate(action.path)}
              >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {React.cloneElement(action.icon, { 
                    style: { 
                      fontSize: 48, 
                      color: action.color 
                    } 
                  })}
                  <Title level={4} style={{ margin: 0 }}>
                    {action.title}
                  </Title>
                  <Paragraph 
                    type="secondary" 
                    style={{ margin: 0, fontSize: 14 }}
                  >
                    {action.description}
                  </Paragraph>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      </Space>
    </Space>
  );
};

export default AdminDashboard; 
